// ============================================================================
// TRIMA ELIGIBILITY — Single source of truth for "can this user do Trima?"
// ============================================================================
// Used in two places:
//   1. End of ComColors questionnaire: should we show the invitation slide?
//   2. Direct route /questionnaire-competences: should we let the user in?
//
// Future extensions (e.g. "already completed Trima" check) plug in here only.
// ============================================================================

import { IUser } from 'types/app';
import { LANGUAGES } from 'constants/global';

export interface TrimaEligibilityCheck {
  /** True only if every condition is satisfied. */
  isEligible: boolean;
  /** First failing reason, for redirect logic / debugging. */
  reason?:
    | 'not-logged-in'
    | 'not-a-valid-profile'
    | 'already-completed'
    | 'not-mobile'
    | 'not-french'
    | 'comcolors-incomplete';
}

interface TrimaEligibilityInput {
  user: IUser | null | undefined;
  language: string;
  isMobile: boolean;
  /**
   * True when all ComColors sub-questionnaires (main, ipsative, gradual,
   * depuis-quand) are at 100% progression. The caller computes this from
   * the current ComColors state.
   */
  isComColorsComplete: boolean;
}

/**
 * Apply the eligibility rules in declaration order. The first failing rule
 * wins and produces the `reason`. This ordering also defines the redirect
 * priority (e.g. not-logged-in → /login is checked before not-french).
 */
export const checkTrimaEligibility = ({
  user,
  language,
  isMobile,
  isComColorsComplete,
}: TrimaEligibilityInput): TrimaEligibilityCheck => {
  isMobile;
  if (!user) {
    return { isEligible: false, reason: 'not-logged-in' };
  }
  if (!user?.profileInfo?.questionnaireDate) {
    return { isEligible: false, reason: 'not-a-valid-profile' };
  }
  if (user.profileInfo?.competenceQuestionnaireDate) {
    return { isEligible: false, reason: 'already-completed' };
  }
  if (language !== LANGUAGES.fr) {
    return { isEligible: false, reason: 'not-french' };
  }
  if (!isComColorsComplete) {
    return { isEligible: false, reason: 'comcolors-incomplete' };
  }
  return { isEligible: true };
};
